/* eslint-disable @typescript-eslint/no-explicit-any */
import { GetIdxPoppups, UpdateIdxPoppupReadTime } from "@/api/clientApi/Common";
import DialogSettingCenter from "@/components/Dialogs/DialogCenter";
import { DialogType } from "@/components/Dialogs/types";
import { useDialog } from "@/components/Dialogs/useDialog";
import { popupType } from "@/enum/component";
import { useClick } from "@/hooks/useClick";
import { useConfigRestrict } from "@/hooks/useConfigRestrict";
import { removeLocalePrefix } from "@/lib/urlUtils.ts";
import { openInviteePopup } from "@/stores/affiliateStore";
import { configStore, noticeConfigStore } from "@/stores/configStore";
import { getUserChannel, userStore } from "@/stores/userStore";
import { ActivityTypeEnum } from "@/types/compontent/base/baseEnum";
import { NoticeContent } from "@/types/config.ts";
import { IdxPoppup } from "@/types/swagger/common";

const { operateDialog, hasDialog } = useDialog();
const {
  getStatusByLogin,
  getStatusByPay,
  getStatusByDeny,
  getStatusByLabelsDeny,
} = useConfigRestrict();
// 常量定义
const NOTICE_CONTENT_KEY = "content";
const THINK_DATA_POPUP_CONFIG_KEY = "ThinkDataPopupConfig";

// 类型定义
type PopupType = 1 | 2; // 1: 运维弹窗, 2: 系统弹窗

export interface InitialPopupStore {
  noticeList: NoticeContent[]; //系统弹窗
  clientList: NoticeContent[]; //运维弹窗
  popupList: IdxPoppup[]; //弹窗列表
  timeoutKey: Record<number, any>;
}
const initialPopupSettings: InitialPopupStore = {
  noticeList: [],
  clientList: [],
  popupList: [],
  timeoutKey: {},
};

// 登录成功弹窗类型
export const loginTypes = [
  // popupType.register, // 4-新人注册
  // popupType.inviteWheelLogin, // 5-邀请转盘已登录
  // popupType.newerRegisterRoulette, // 6-新人注册转盘
  popupType.systemPopupLogin, // 7-系统弹窗已登录
  popupType.reliefFund, // 8-救济金
  // popupType.jackpot, // 9-jackpot宝藏
  popupType.redPocket, // 10-红包雨
  // popupType.bottomApp, // 11-底部APP下载
  // popupType.webToApp, // 12-APP下载活动 // 此项不开, app 下载活动由优惠活动开关管理
  // popupType.callback, // 13-召回奖励
  // popupType.subscribe, // 14-订阅活动
  // popupType.deposit, // 15-新用户连续充值活动
  popupType.inviteeRewardLogin, // 19-团队邀请弹窗配置已登录
  // popupType.registerSuccessPopup, // 20-注册成功保存信息弹窗
  popupType.newUserPromotion, // 21-新人优惠礼包
  // popupType.appDownload, // 24-APP下载
  // popupType.pwaDownload, // 25-PWA下载
  popupType.firstDepositLogin, // 27-首存活动已登录
  popupType.flipCardLogin, // 28-翻卡活动已登录
  popupType.vipWeeklyCardLogin, // 30-超值七日卡 已登陆
  popupType.dailyLossBackLogin, // 31-每日亏损返还活动已登录
  popupType.vipDayDeposit, // 33-vip每日首存
];

// 未登录弹窗类型
export const noLoginTypes = [
  // popupType.registerRoulette, // 1-注册转盘
  // popupType.experience, // 2-体验金
  // popupType.inviteWheelNoLogin, // 3-邀请转盘未登录
  popupType.systemPopupNoLogin, // 17-系统弹窗配置未登录
  popupType.inviteeRewardNoLogin, // 18-团队邀请弹窗配置未登录
  // popupType.appDownloadNoLogin, // 22-APP下载未登陆
  // popupType.pwaDownloadNoLogin, // 23-PWA下载未登陆
  popupType.firstDepositNoLogin, // 26-首存活动未登录
  popupType.flipCardNoLogin, // 29-翻卡活动未登录
];

/**
 * 过滤弹窗列表
 *
 * @param popupList 弹窗列表
 * @returns 过滤后的弹窗列表
 */
export const filterPopupList = (
  popupList: NoticeContent[],
): NoticeContent[] => {
  return popupList.filter((item: NoticeContent) => {
    return (
      !item.hide &&
      getStatusByLogin(item.login_type_limit) &&
      getStatusByPay(item.pay_type) &&
      getStatusByDeny(item.general_list || [], item.deny_pack_type) &&
      getStatusByLabelsDeny(item.label_list || [], item.label_type_limit)
    );
  });
};

/**
 * 打开系统运维弹窗
 *
 * @param type 弹窗类型：1-运维弹窗，2-系统弹窗
 * @param id 弹窗ID（仅运维弹窗需要）
 */
export const openSystemPopup = (type: PopupType, id?: number): void => {
  try {
    const popups = JSON.parse(JSON.stringify(initialPopupSettings));
    if (type === 1) {
      // 运营弹窗
      const popupList =
        configStore.frontCfgMap[THINK_DATA_POPUP_CONFIG_KEY]?.["content"] ?? [];
      const targetPopup = popupList.find(
        (item: NoticeContent) => item.id == id,
      );
      if (targetPopup) {
        const newTargetPopup = {
          ...targetPopup,
        };
        popups.clientList = [...(popups.clientList ?? []), newTargetPopup];
      } else {
        console.warn(`System popup not found with id: ${id}`);
      }
    } else {
      // 系统弹窗
      if (!noticeConfigStore?.status) return;
      const popupList = noticeConfigStore?.[NOTICE_CONTENT_KEY] ?? [];
      const filteredList = filterPopupList(popupList);

      popups.noticeList = filteredList ?? [];
    }

    // 如果有通知列表且当前没有显示系统弹窗，则打开弹窗
    if (
      (popups.noticeList?.length || popups.clientList?.length) &&
      !hasDialog("SystemAndClient")
    ) {
      const pathname = window.location.pathname;
      // 判断是否首页
      if (removeLocalePrefix(pathname) === "/") {
        operateDialog("SystemAndClient", true, popups);
      }
    }
  } catch (error) {
    console.error("Error opening system popup:", error);
  }
};

/** 将弹窗数据注册到 DialogCenter */
const registerPopupsToDialogCenter = (popups: IdxPoppup[]): void => {
  const { showDialog } = useDialog();

  // 获取弹窗对应的 DialogType
  // 弹窗类型配置映射
  const popupTypeConfig = {
    // 1-注册转盘
    [popupType.registerRoulette]: {
      dialogType: undefined,
      action: () => {},
    },
    // 2-体验金
    [popupType.experience]: {
      dialogType: undefined,
      action: () => {},
    },
    // 3-邀请转盘未登录
    [popupType.inviteWheelNoLogin]: {
      dialogType: undefined,
      action: () => {},
    },
    // 4-新人注册
    [popupType.register]: {
      dialogType: undefined,
      action: () => {},
    },
    // 5-邀请转盘已登录
    [popupType.inviteWheelLogin]: {
      dialogType: undefined,
      action: () => {},
    },
    // 6-新人注册转盘
    [popupType.newerRegisterRoulette]: {
      dialogType: undefined,
      action: () => {},
    },
    // 7-系统弹窗已登录
    [popupType.systemPopupLogin]: {
      dialogType: DialogType.SystemAndClient,
      action: () => openSystemPopup(2),
    },
    // 8-救济金
    [popupType.reliefFund]: {
      dialogType: DialogType.FreeRelief,
      action: () => showDialog({ type: DialogType.FreeRelief }),
    },
    // 9-Jackpot 宝藏
    [popupType.jackpot]: {
      dialogType: undefined,
      action: () => {},
    },
    // 10-红包雨
    [popupType.redPocket]: {
      dialogType: undefined,
      action: () =>
        showDialog({
          type: DialogType.ActivityIframe,
          data: { atype: ActivityTypeEnum.shakeRedPacket },
        }),
    },
    // 11-底部APP下载
    [popupType.bottomApp]: {
      dialogType: undefined,
      action: () => {},
    },
    // 12-APP下载活动
    // [popupType.webToApp]: { dialogType: DialogType.AppDownloadActivity, action: () => showDialog({ type: DialogType.AppDownloadActivity }) },
    // 13-召回奖励
    [popupType.callback]: {
      dialogType: undefined,
      action: () => {},
    },
    // 14-订阅活动
    [popupType.subscribe]: {
      dialogType: undefined,
      action: () => {},
    },
    // 15-新用户连续充值活动
    [popupType.deposit]: {
      dialogType: undefined,
      action: () => {},
    },
    // 16-注册转盘已登录
    [popupType.registerRouletteLogin]: {
      dialogType: undefined,
      action: () => {},
    },
    // 17-系统弹窗配置未登录
    [popupType.systemPopupNoLogin]: {
      dialogType: DialogType.SystemAndClient,
      action: () => openSystemPopup(2),
    },
    // 18-团队邀请弹窗配置未登录
    [popupType.inviteeRewardNoLogin]: {
      dialogType: DialogType.TeamInvitee,
      action: () => openInviteePopup(),
    },
    // 19-团队邀请弹窗配置已登录
    [popupType.inviteeRewardLogin]: {
      dialogType: DialogType.TeamInvitee,
      action: () => openInviteePopup(),
    },
    // 20-注册成功保存信息弹窗
    [popupType.registerSuccessPopup]: {
      dialogType: undefined,
      action: () => {},
    },
    // 21-新人优惠礼包
    [popupType.newUserPromotion]: {
      dialogType: DialogType.SuperValueGift,
      action: () => showDialog({ type: DialogType.SuperValueGift }),
    },
    // 22-APP下载未登录
    [popupType.appDownloadNoLogin]: {
      dialogType: undefined,
      action: () => {},
    },
    // 23-PWA下载未登录
    [popupType.pwaDownloadNoLogin]: {
      dialogType: undefined,
      action: () => {},
    },
    // 24-APP下载
    [popupType.appDownload]: {
      dialogType: undefined,
      action: () => {},
    },
    // 25-PWA下载
    [popupType.pwaDownload]: {
      dialogType: undefined,
      action: () => {},
    },
    // 26-首存活动未登录
    [popupType.firstDepositNoLogin]: {
      dialogType: DialogType.FirstDeposit,
      action: () =>
        showDialog({
          type: DialogType.ActivityIframe,
          data: { atype: ActivityTypeEnum.firstDeposit },
        }),
    },
    // 27-首存活动已登录
    [popupType.firstDepositLogin]: {
      dialogType: DialogType.ActivityIframe, // 修复：使用正确的 dialogType
      action: () => {
        if (userStore?.userInfo?.is_pay === 0) {
          showDialog({
            type: DialogType.ActivityIframe,
            data: { atype: ActivityTypeEnum.firstDeposit },
          });
        }
      },
    },
    // 28-翻卡活动已登录
    [popupType.flipCardLogin]: {
      dialogType: DialogType.ActivityIframe,
      action: () => {
        showDialog({
          type: DialogType.ActivityIframe,
          data: { atype: ActivityTypeEnum.flipCard, source: "modal" },
        });
      },
    },
    // 29-翻卡活动未登录
    [popupType.flipCardNoLogin]: {
      dialogType: DialogType.ActivityIframe,
      action: () => {
        showDialog({
          type: DialogType.ActivityIframe,
          data: { atype: ActivityTypeEnum.flipCard, source: "modal" },
        });
      },
    },
    // 30-超值七日卡 已登陆
    [popupType.vipWeeklyCardLogin]: {
      dialogType: DialogType.ActivityIframe,
      action: () => {
        showDialog({
          type: DialogType.ActivityIframe,
          data: { atype: ActivityTypeEnum.vipWeeklyCard, source: "modal" },
        });
      },
    },
    // 31-每日亏损返还活动已登录
    [popupType.dailyLossBackLogin]: {
      dialogType: DialogType.ActivityIframe,
      action: () => {
        showDialog({
          type: DialogType.ActivityIframe,
          data: { atype: ActivityTypeEnum.dailyLossBack, source: "modal" },
        });
      },
    },
    // 33-vip每日首充
    [popupType.vipDayDeposit]: {
      dialogType: DialogType.ActivityIframe,
      action: () => {
        showDialog({
          type: DialogType.ActivityIframe,
          data: { atype: ActivityTypeEnum.vipDayDeposit, source: "modal" },
        });
      },
    },
  } as const;

  // 记得也要同步打开 LoginType / NoLoginType 才会有办法对应的内容
  const convertedPopups = popups?.map((popup) => {
    const config =
      popupTypeConfig[popup.poppup_type as keyof typeof popupTypeConfig];

    return {
      execute: async () => {
        // 弹窗打开时立即调用 UpdateIdxPoppupReadTime
        try {
          await UpdateIdxPoppupReadTime({ poppup_type: popup.poppup_type });
        } catch (error) {
          console.error("❌ Error calling UpdateIdxPoppupReadTime:", error);
        }

        config?.action?.();
      },
      dialogType: config?.dialogType,
      countdown: popup.countdown,
      sort: popup.sort,
    };
  });

  DialogSettingCenter.registerPopups(convertedPopups);
};

/** 获取首页弹窗数据 */
export const getHomePopups = async (): Promise<void> => {
  try {
    const types = userStore.isLogin ? loginTypes : noLoginTypes;
    const { general, channel } = getUserChannel();
    const params = {
      data: {
        poppup_types: types,
        pack: channel,
        general: general,
        labels: userStore.userInfo?.labels || [],
      },
      isNeedLogin: Boolean(userStore.isLogin),
    };

    const response = await GetIdxPoppups(params);

    if (response?.poppups?.length) {
      const sortedPopups = response.poppups.sort(
        (a: IdxPoppup, b: IdxPoppup) => b.sort - a.sort,
      );

      // setPopupSettings((prev) => ({
      //   ...prev,
      //   popupList: sortedPopups,
      // }));

      if (sortedPopups?.length) {
        // 注册到 DialogCenter 調用
        registerPopupsToDialogCenter(sortedPopups);
        console.log("🔔 registerPopupsToDialogCenter", sortedPopups);
        // 旧方法先保留
        // await getPopupsData()
      }
    }
  } catch (error) {
    console.error("Error fetching home popups:", error);
  }
};

// 旧方法先保留 0815, popupStore 已弃用 改使预 signal 管理
/** 获取并处理弹窗数据 */
// export const getPopupsData = async (): Promise<void> => {
//   try {
//     const currentPopup = popupStore.popupList?.[0];
//     if (!currentPopup) return;

//     if (currentPopup?.countdown) {
//       const timeoutId = setTimeout(() => {
//         startOpenPopup(currentPopup);
//         UpdateIdxPoppupReadTime({ poppup_type: currentPopup.poppup_type });
//       }, currentPopup?.countdown * 1000);
//       setPopupStore("timeoutKey", currentPopup.poppup_type, timeoutId);
//     } else {
//       startOpenPopup(currentPopup);
//     }
//   } catch (error) {
//     console.error("Error processing popup data:", error);
//   }
// };

/**
 * 打开弹窗
 *
 * @param data 弹窗数据
 */
// export const startOpenPopup = (data: IdxPoppup): void => {
//   try {
//     const type = data.poppup_type || -1;

//     switch (type) {
//       case popupType.systemPopupLogin:
//       case popupType.systemPopupNoLogin:
//         openSystemPopup(2);
//         break;
//       case popupType.inviteeRewardNoLogin:
//       case popupType.inviteeRewardLogin:
//         openInviteePopup();
//         break;
//       default:
//         console.warn(`Unknown popup type: ${type}`);
//         break;
//     }
//   } catch (error) {
//     console.error("Error starting popup:", error);
//   }
// };

export function usePopup() {
  const { click } = useClick();

  /**
   * 处理弹窗点击事件
   *
   * @param item 弹窗内容
   */
  const onNoticeFun = (item: NoticeContent): void => {
    if (!item.click_id) return;

    try {
      operateDialog("SystemAndClient");
      // 增加未登录跳存款等页面 统一跳转登录页
      if (["deposit"].includes(item.click_params) && !userStore.isLogin) {
        click(item.click_id, "login");
      } else {
        click(item.click_id, item.click_params);
      }
    } catch (error) {
      console.error("Error handling notice click:", error);
    }
  };
  return {
    onNoticeFun,
  };
}
